/**
 * useDeadPanelDetector (PHASE 5)
 * ==============================
 * Prevents the left panel from freezing by detecting silent SSE stalls and
 * safely re-enabling polling.
 *
 * Contract:
 * - If `_progressStreamConnected` is true but no SSE events arrive for N seconds,
 *   flip `_progressStreamConnected` to false (so `useNotebookPolling` resumes).
 * - Never throws; never creates runaway loops.
 */

import { useEffect, useRef } from 'react';

const DEFAULT_STALE_MS = 45_000; // 45s without SSE events = treat as dead stream
const DEFAULT_MIN_INTERVAL_MS = 20_000; // avoid flip-flopping too frequently

export default function useDeadPanelDetector({
  selectedNotebook,
  setSelectedNotebook,
  staleMs = DEFAULT_STALE_MS,
  minIntervalMs = DEFAULT_MIN_INTERVAL_MS,
  onDegraded,
}) {
  const lastTriggeredAtRef = useRef(0);

  useEffect(() => {
    const nb = selectedNotebook;
    if (!nb?.id) return;

    const isProcessing = ['processing', 'uploading'].includes(String(nb.status || '').toLowerCase());
    const connected = nb._progressStreamConnected === true;

    if (!isProcessing || !connected) return;

    const t = setInterval(() => {
      try {
        const cur = selectedNotebook;
        if (!cur?.id) return;
        if (cur._progressStreamConnected !== true) return;
        const curProcessing = ['processing', 'uploading'].includes(String(cur.status || '').toLowerCase());
        if (!curProcessing) return;

        const last = typeof cur._lastSseEventAt === 'number' ? cur._lastSseEventAt : 0;
        const age = last > 0 ? Date.now() - last : Infinity;
        if (age < staleMs) return;

        const now = Date.now();
        if (now - lastTriggeredAtRef.current < minIntervalMs) return;
        lastTriggeredAtRef.current = now;

        // Flip back to polling mode (prevents left panel freeze)
        setSelectedNotebook(prev => {
          if (!prev || prev.id !== cur.id) return prev;
          return {
            ...prev,
            _progressStreamConnected: false,
            _sseStaleDetectedAt: now,
          };
        });

        onDegraded?.({
          code: 'SSE_STALE',
          message: 'Live updates stalled. Switching to polling to prevent UI freeze.',
          details: { staleMs, lastSseEventAt: last },
        });
      } catch {
        // never crash UI from the detector
      }
    }, 5000);

    return () => clearInterval(t);
  }, [selectedNotebook, setSelectedNotebook, staleMs, minIntervalMs, onDegraded]);
}

